import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Newspaper } from "lucide-react";
import { getPosts } from "@/data/blogPosts";
import { useSlowFactor } from "@/hooks/useSlowFactor";
import { useLanguage } from "@/i18n/LanguageContext";

export const BlogSection = () => {
  const slow = useSlowFactor();
  const { t, lang } = useLanguage();
  const blogPosts = getPosts(lang);
  const fmtDate = (d) =>
    new Date(d).toLocaleDateString(t.locale, { day: "numeric", month: "long", year: "numeric" });

  return (
    <section id="blog" data-testid="blog-section" className="py-16 sm:py-24 lg:py-32 carbon border-t border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-red-500 mb-4 flex items-center gap-3">
            <Newspaper size={16} strokeWidth={2} /> {t.blog.eyebrow}
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight leading-tight text-white">
            {t.blog.heading}
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogPosts.map((post, i) => (
            <motion.div
              key={post.slug}
              data-testid={`blog-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 * slow, delay: i * 0.1 * slow }}
            >
              <Link
                to={`/blog/${post.slug}`}
                data-testid={`blog-link-${i}`}
                className="group flex flex-col h-full bg-zinc-900/40 border border-zinc-800 hover:border-red-500 transition-colors"
              >
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold uppercase tracking-wide px-2.5 py-1">
                    {fmtDate(post.date)}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display font-bold text-xl uppercase tracking-tight text-white leading-tight mb-3 group-hover:text-red-500 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-zinc-300 leading-relaxed text-sm flex-1">{post.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-red-500 font-bold uppercase tracking-wide text-sm">
                    {t.blog.read} <ArrowUpRight size={16} strokeWidth={2.5} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
